import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_PREV_TOKEN = 'northline_support_prev_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const lastReadOnlyNotice = useRef(0);
  const interceptorRef = useRef(null);

  const logout = useCallback(() => {
    // SEC-1b — sign out ends the session on the server too (the token is
    // revoked); clearing the browser alone left it valid for up to 24 hours.
    const current = localStorage.getItem('token');
    if (current) {
      axios.post(`${API_URL}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${current}` } }).catch(() => {});
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPermissions([]);
  }, []);

  // Global axios 401 interceptor — auto-logout on expired/invalid tokens
  useEffect(() => {
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        const rawDetail = error.response?.data?.detail;
        // A detail may be an object ({code, message}); read its text, never call
        // string methods on the object (that threw inside this handler).
        const detail = typeof rawDetail === 'string' ? rawDetail : (rawDetail?.message || '');
        const status = error.response?.status;
        // SEC-1b — a team member without two-factor sign-in sets it up first.
        if (status === 403 && rawDetail && rawDetail.code === 'MFA_ENROLLMENT_REQUIRED') {
          if (!window.location.pathname.startsWith('/security/two-factor')) {
            window.location.href = '/security/two-factor';
          }
          return Promise.reject(error);
        }
        // BILL-4b — the central read-only gate refused a write. Say so once
        // (not once per failing request), with the way out.
        if (status === 403 && error.response?.data?.code === 'support_read_only') {
          toast.error('This support session is read-only', {
            description: 'The customer did not allow changes when granting access.',
          });
          return Promise.reject(error);
        }
        if (status === 403 && error.response?.data?.code === 'company_read_only') {
          const now = Date.now();
          if (now - lastReadOnlyNotice.current > 10000) {
            lastReadOnlyNotice.current = now;
            toast.error('Northline is currently in Read-Only Mode', {
              description: error.response.data.access?.reason || 'Reactivate your subscription to make changes.',
              action: { label: 'Reactivate Subscription', onClick: () => { window.location.href = '/pricing'; } },
            });
          }
          return Promise.reject(error);
        }
        // 401 — token expired / signature invalid / session_version /
        //   membership_version mismatch. 403 with these specific
        //   server-side hints means the JWT is no longer trustable
        //   for this user/company pair (membership deactivated, no
        //   company scope, etc.) — also force a clean re-login.
        const isAuthInvalid =
          status === 401 ||
          (status === 403 && (
            detail.includes('Inactive company membership') ||
            detail.includes('Token missing company scope') ||
            detail.includes('Membership state changed') ||
            detail.includes('Session expired')
          ));
        if (
          isAuthInvalid &&
          !url.includes('/api/auth/login') &&
          !url.includes('/api/auth/logout') &&
          !url.includes('/api/auth/me') &&
          !url.includes('/api/auth/companies') &&
          !url.includes('/marketplace/') &&
          !url.includes('/tenant-portal/') &&
          !url.includes('/contractor-portal/') &&
          !url.includes('/portal-invites/')
        ) {
          logout();
          window.location.href = '/login?expired=1';
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [logout]);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setUser(data);
      setPermissions(data.permissions || []);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    // If the backend reports MFA is required, surface that to the caller
    // WITHOUT setting the user/token — the LoginPage will route into the
    // MFA code-entry step and call verifyMfa() to complete sign-in.
    if (response.data?.mfa_required) {
      return { mfa_required: true, mfa_token: response.data.mfa_token };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setPermissions(userData.permissions || []);
    return userData;
  };

  const verifyMfa = async (mfa_token, code) => {
    const response = await axios.post(`${API_URL}/api/auth/mfa/verify`, { mfa_token, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setPermissions(userData.permissions || []);
    return userData;
  };

  const signup = async (payload) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setPermissions(userData.permissions || []);
    return response.data;
  };

  // ":edit" / ":create" / ":delete" / ":approve_payment" / ":resolve" / ":reports"
  // all imply ":view" — you can't perform a higher-privilege action without
  // implicitly being able to see the resource. This avoids the trap where an
  // admin grants only `:edit` and the user ends up with the tab hidden.
  const _ELEVATED_SUFFIXES = [
    ':edit', ':create', ':delete', ':approve_payment',
    ':resolve', ':reports', ':assign', ':change_stage',
  ];

  const hasPermission = useCallback((key) => {
    if (permissions.includes(key)) return true;
    if (typeof key === 'string' && key.endsWith(':view')) {
      const prefix = key.slice(0, -':view'.length);
      return _ELEVATED_SUFFIXES.some(s => permissions.includes(prefix + s));
    }
    return false;
  }, [permissions]);

  const hasAnyPermission = useCallback((keys) => {
    return keys.some(k => hasPermission(k));
  }, [hasPermission]);

  // BILL-7 — Northline staff enter a customer's company only through a
  // support token the customer's grant allows; the staff member's own token
  // is set aside and restored when the session ends.
  const startSupportSession = (supportToken) => {
    const own = localStorage.getItem('token');
    if (own) localStorage.setItem(SUPPORT_PREV_TOKEN, own);
    localStorage.setItem('token', supportToken);
    setToken(supportToken);
  };

  const endSupportSession = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/support/sessions/end`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // The session may already have ended with its grant; restore anyway.
    }
    const own = localStorage.getItem(SUPPORT_PREV_TOKEN);
    localStorage.removeItem(SUPPORT_PREV_TOKEN);
    if (own) {
      localStorage.setItem('token', own);
      setToken(own);
    } else {
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, verifyMfa, signup, logout, token, permissions, hasPermission, hasAnyPermission, startSupportSession, endSupportSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
